import React, { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { api, describeError } from "@/lib/api";
import { toast } from "sonner";

// "Change" beside your name on the profile. Checks the name is free while
// you type; the server checks again on save (PUT /me/username) and rewrites
// the copies of your name in games and challenges (backend/usernames.py).
export default function ChangeUsername() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [check, setCheck] = useState(null); // {name, ok, message}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const current = user?.username || "";
  const wanted = name.trim();
  const unchanged = wanted === current;

  useEffect(() => {
    if (!open || !wanted || unchanged) { setCheck(null); return; }
    let live = true;
    const t = setTimeout(() => {
      api.get("/me/username/check", { params: { name: wanted } })
        .then(({ data }) => { if (live) setCheck({ name: wanted, ok: data.ok, message: data.message }); })
        .catch(() => {});
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [open, wanted, unchanged]);

  const close = (o) => {
    if (busy) return;
    setOpen(o);
    if (o) setName(current);
    else { setError(""); setCheck(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (unchanged) { setOpen(false); return; }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.put("/me/username", { username: wanted });
      setUser(data);
      setOpen(false);
      toast.success(`You're now ${data.username}.`);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!user || user.is_guest) return null;
  const fresh = check && check.name === wanted ? check : null;

  return (
    <>
      <button
        type="button"
        onClick={() => close(true)}
        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground hover:underline"
        data-testid="change-username"
      >
        <Pencil className="w-3 h-3" /> Change
      </button>

      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change your username</DialogTitle>
            <DialogDescription>
              Your games, friends, chats and rating stay the same. Friends will see your new name.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input
              autoFocus
              autoComplete="username"
              maxLength={24}
              value={name}
              onChange={(e) => { setName(e.target.value); setError(""); }}
              className="h-11"
              data-testid="change-username-input"
            />
            <div className="text-sm min-h-[1.25rem]" data-testid="change-username-status">
              {error ? (
                <span className="text-destructive">{error}</span>
              ) : fresh ? (
                fresh.ok
                  ? <span className="text-emerald-600 dark:text-emerald-400">✓ {fresh.name} is free</span>
                  : <span className="text-destructive">{fresh.message}</span>
              ) : null}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => close(false)} disabled={busy}>Cancel</Button>
              <Button
                type="submit"
                disabled={busy || !wanted || unchanged || (fresh && !fresh.ok)}
                className="rounded-full"
                data-testid="change-username-save"
              >
                {busy ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
